import { EditorRow, TextFieldStyled } from 'components/common/styles';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ModalWrapper } from '../../../components/ModalWrapper';
import ModuleContext from '../../../CMFW/context/ModuleContext';
import AudioSetsModel from '../../../models/AudioSetsModel';
import AudioSetsItemModel, { IAudioSetsItem } from '../../../models/AudioSetsItemModel';
import { faAngleLeft, faAngleRight, faPlay, faScissors } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import { Loading } from '../../../components/common/Loading';
import WaveSurfer from 'wavesurfer.js';
import { AUDIO_DONE, AUDIO_REJECTED, AUDIO_REVIEWING } from '../audioReviewNavigation';
// @ts-ignore WaveSurfer v4 plugin has no TypeScript declaration.
import * as WaveSurferRegions from 'wavesurfer.js/dist/plugin/wavesurfer.regions';

type IProps = {
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  item: IAudioSetsItem;
  reviewEnabled: boolean;
  index: number;
  total: number;
  modelItems: AudioSetsItemModel;
  /** Set voiced by TTS: shows the prompt and the Redo button. */
  isTts?: boolean;
  /** Prompt the set was voiced with (default for the redo prompt). */
  ttsPrompt?: string;
  /** This item is being (re)generated right now: no audio to play yet. */
  regenerating?: boolean;
  onRedo?: (itemId: number, prompt: string, ipaWord?: string, ipa?: string, readAs?: string) => void;
  onReviewed: (itemId: number, ok: boolean, note: string) => void;
  items: IAudioSetsItem[];
  regeneratingIds: Set<number>;
  onSelectIndex: (index: number) => void;
};

const AudioReviewItem: React.FC<IProps> = (props) => {
  const [note, setNote] = useState(props.item.note);
  const [prompt, setPrompt] = useState(props.ttsPrompt || '');
  // Pronunciation override for the redo: one word of the text + its IPA (voiced by Chirp 3 HD, same voice).
  const [ipaWord, setIpaWord] = useState('');
  const [ipa, setIpa] = useState('');
  // Reading override for the redo: voiced instead of the item's text, which stays as written in the course.
  const [readAs, setReadAs] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [cutMode, setCutMode] = useState(false);
  const [cutting, setCutting] = useState(false);
  const waveSurfer = useRef<WaveSurfer>();
  const cutModeRef = useRef(false);
  const cuttingRef = useRef(false);
  const deleteRangeHandler = useRef<(region: any) => void>();

  const moduleModel = useContext(ModuleContext) as AudioSetsModel;

  const initWave = useCallback(
    (node) => {
      if (node) {
        if (!waveSurfer.current) {
          waveSurfer.current = WaveSurfer.create({
            container: node,
            plugins: [WaveSurferRegions.create({ maxRegions: 1 })],
          });
          waveSurfer.current.on('ready', () => {
            if (waveSurfer.current) {
              waveSurfer.current.play();
            }
          });
          waveSurfer.current.on('region-update-end', (region: any) => {
            deleteRangeHandler.current && deleteRangeHandler.current(region);
          });
        }

        if (!props.regenerating) {
          waveSurfer.current && waveSurfer.current.load(props.item.audioPath + '?dc=' + new Date().getTime());
        }
      }
    },
    [props.item.audioPath, props.regenerating],
  );

  useEffect(() => {
    return () => waveSurfer.current && waveSurfer.current.destroy();
  }, []);

  useEffect(() => {
    setNote(props.item.note);
    // each item starts from the set's prompt; the adjusted prompt is not persisted (decision 17 aug 2026)
    setPrompt(props.ttsPrompt || '');
    setIpaWord('');
    setIpa('');
    setReadAs('');
    cutModeRef.current = false;
    setCutMode(false);
    (waveSurfer.current as any)?.disableDragSelection?.();
    waveSurfer.current?.clearRegions?.();
  }, [props.item.id, props.item.note, props.ttsPrompt]);

  deleteRangeHandler.current = async (region: any) => {
    if (!cutModeRef.current || cuttingRef.current) return;
    if (region.end - region.start < 0.05) {
      region.remove();
      return;
    }

    cuttingRef.current = true;
    cutModeRef.current = false;
    setCutting(true);
    setCutMode(false);
    waveSurfer.current?.stop();
    (waveSurfer.current as any)?.disableDragSelection?.();
    const result = await moduleModel.deleteItemAudioRange(
      props.item.id,
      Math.round(region.start * 1000),
      Math.round(region.end * 1000),
    );
    region.remove();
    if (result && waveSurfer.current) {
      waveSurfer.current.load(result.audioPath + '?dc=' + Date.now());
    }
    cuttingRef.current = false;
    setCutting(false);
  };

  const toggleCutMode = () => {
    if (!waveSurfer.current || cutting || props.regenerating) return;
    const enabled = !cutModeRef.current;
    cutModeRef.current = enabled;
    setCutMode(enabled);
    waveSurfer.current.clearRegions?.();
    if (enabled) {
      (waveSurfer.current as any).enableDragSelection({
        color: 'rgba(229, 57, 53, 0.3)',
        drag: false,
        resize: false,
      });
    } else {
      (waveSurfer.current as any).disableDragSelection();
    }
  };

  const reviewItem = async (ok: boolean) => {
    setSaving(true);
    waveSurfer.current && waveSurfer.current.stop();
    const result = await moduleModel.reviewItem(props.item.id, ok, note);
    if (result) {
      props.onReviewed(props.item.id, ok, note);
    }
    if (result && result.allReviewed) {
      if (result.newSetId === 0) {
        setMessage('Revised Set');
      } else {
        setMessage('A revision set has been created: ' + result.newSetName);
        await moduleModel.updateGridRow(result.newSetId, 'add');
      }
    }
    setSaving(false);
    await props.modelItems.updateGridRow(props.item.id);
  };

  const handleOK = async () => {
    await reviewItem(true);
  };

  const handleBad = async () => {
    await reviewItem(false);
  };

  /** Redo with the (possibly adjusted) prompt and move on: the clip regenerates in the background. */
  const handleRedo = () => {
    waveSurfer.current && waveSurfer.current.stop();
    props.onRedo && props.onRedo(props.item.id, prompt, ipaWord.trim(), ipa.trim(), readAs.trim());
    props.onNext();
  };

  const handlePlay = () => {
    waveSurfer.current && waveSurfer.current.playPause();
  };

  const onNoteChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNote(event.target.value);
  };

  const reviewed = props.items.filter((item) => item.status === AUDIO_DONE || item.status === AUDIO_REJECTED).length;
  const pending = props.items.length - reviewed;

  const progressClass = (item: IAudioSetsItem, index: number) => {
    const classes = [] as string[];
    if (props.regeneratingIds.has(item.id)) {
      classes.push('generating');
    } else if (item.status === AUDIO_DONE) {
      classes.push('done');
    } else if (item.status === AUDIO_REJECTED) {
      classes.push('rejected');
    } else if (item.status === AUDIO_REVIEWING) {
      classes.push('pending');
    } else {
      classes.push('doing');
    }
    if (index === props.index) {
      classes.push('current');
    }
    return classes.join(' ');
  };

  return (
    <ModalWrapper onClose={props.onClose} title={'Review'} width={720} height={props.isTts ? 520 : 430}>
      {message !== '' ? (
        <p>{message}</p>
      ) : (
        <>
          <ReviewProgress aria-label="Audio review progress">
            <ProgressSquares>
              {props.items.map((item, index) => (
                <ProgressSquare
                  key={item.id}
                  className={progressClass(item, index)}
                  onClick={cutting ? undefined : () => props.onSelectIndex(index)}
                  disabled={cutting}
                  title={`${index + 1}: ${progressClass(item, index).replace(' current', '')}`}
                  aria-label={`Open audio ${index + 1}`}
                />
              ))}
            </ProgressSquares>
            <ProgressText>
              {reviewed} reviewed · {pending} pending
            </ProgressText>
          </ReviewProgress>
          <Container>
            {props.regenerating ? (
              <Regenerating>
                <CircularProgress size={20} /> regenerating…
              </Regenerating>
            ) : (
              <WaveformWrapper ref={initWave} />
            )}
            <ItemText dangerouslySetInnerHTML={{ __html: props.item.text }} />
            {!props.regenerating && (
              <PlaybackControls>
                {props.reviewEnabled && props.item.status === AUDIO_REVIEWING && (
                  <CutButton
                    type="button"
                    onClick={toggleCutMode}
                    disabled={cutting}
                    className={cutMode ? 'active' : ''}
                    title={cutMode ? 'Cancel range selection' : 'Delete an audio range'}
                    aria-label="Delete an audio range"
                  >
                    {cutting ? <CircularProgress size={18} /> : <FontAwesomeIcon icon={faScissors} />}
                  </CutButton>
                )}
                <FontAwesomeIconPlay icon={faPlay} onClick={handlePlay} size={'2x'} />
              </PlaybackControls>
            )}
            {cutMode && <CutHint>Drag over the waveform to delete that range · Click the scissors to cancel</CutHint>}
          </Container>
          {props.reviewEnabled && props.isTts && (
            <EditorRow>
              <PromptField
                value={prompt}
                label={'Prompt'}
                variant="outlined"
                multiline
                rows={2}
                onChange={(event) => setPrompt(event.target.value)}
              />
              <ButtonStyled
                variant="contained"
                size="small"
                onClick={handleRedo}
                disabled={
                  props.regenerating || cutting || prompt.trim() === '' || (ipaWord.trim() === '') !== (ipa.trim() === '')
                }
              >
                Redo
              </ButtonStyled>
            </EditorRow>
          )}
          {props.reviewEnabled && props.isTts && (
            <EditorRow>
              <IpaField
                value={readAs}
                label={'Read as'}
                variant="outlined"
                size="small"
                placeholder="twenty thirty"
                title="Voices this text instead of the item's; the text in the course does not change"
                onChange={(event) => setReadAs(event.target.value)}
              />
              <IpaField
                value={ipaWord}
                label={'Word'}
                variant="outlined"
                size="small"
                placeholder="word of the text"
                onChange={(event) => setIpaWord(event.target.value)}
              />
              <IpaField
                value={ipa}
                label={'IPA'}
                variant="outlined"
                size="small"
                placeholder="ˈʃtɛːpçən"
                title="Forces this word's pronunciation: the clip is voiced by Chirp 3 HD (same voice) instead of Gemini"
                onChange={(event) => setIpa(event.target.value)}
              />
            </EditorRow>
          )}
          {props.reviewEnabled && (
            <EditorRow>
              {!props.isTts && (
                <>
                  <TextFieldStyled value={note} label={'Note'} variant="outlined" onChange={onNoteChange} />
                  <ButtonStyled
                    variant="contained"
                    size="small"
                    color="secondary"
                    onClick={handleBad}
                    disabled={cutting || note.length === 0}
                  >
                    Bad
                  </ButtonStyled>
                </>
              )}
              <ButtonStyled variant="contained" size="small" color="primary" onClick={handleOK} disabled={cutting}>
                Good
              </ButtonStyled>
            </EditorRow>
          )}
          <EditorRowProgress>
            <FontAwesomeIconProgress icon={faAngleLeft} onClick={cutting ? undefined : props.onPrev} />
            {props.index + 1}/{props.total}
            <FontAwesomeIconProgress icon={faAngleRight} onClick={cutting ? undefined : props.onNext} />
          </EditorRowProgress>
          {saving && <Loading />}
        </>
      )}
    </ModalWrapper>
  );
};

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const EditorRowProgress = styled(EditorRow)`
  justify-content: flex-end;
  margin: 10px;
`;

const ReviewProgress = styled.div`
  margin: 0 10px 10px;
`;

const ProgressSquares = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  max-height: 54px;
  overflow-y: auto;
  padding: 3px;
`;

const ProgressSquare = styled.button`
  width: 14px;
  height: 14px;
  min-width: 14px;
  padding: 0;
  border: 1px solid #9e9e9e;
  border-radius: 2px;
  background: #eeeeee;
  cursor: pointer;

  &.pending {
    background: #fbc02d;
  }

  &.done {
    background: #43a047;
  }

  &.rejected {
    background: #e53935;
  }

  &.generating,
  &.doing {
    background: #1e88e5;
  }

  &.generating {
    animation: progress-pulse 1s ease-in-out infinite alternate;
  }

  &.current {
    box-shadow: 0 0 0 2px #212121;
  }

  @keyframes progress-pulse {
    from {
      opacity: 0.35;
    }
    to {
      opacity: 1;
    }
  }
`;

const ProgressText = styled.div`
  margin-top: 5px;
  color: #616161;
  font-size: 12px;
  text-align: right;
`;

const FontAwesomeIconProgress = styled(FontAwesomeIcon)`
  cursor: pointer;
  margin-left: 5px;
  margin-right: 5px;
`;

const FontAwesomeIconPlay = styled(FontAwesomeIcon)`
  cursor: pointer;
`;

const PlaybackControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 5px auto;
`;

const CutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 30px;
  margin-right: 24px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  background: #fff;
  color: #616161;
  cursor: pointer;

  &.active {
    border-color: #e53935;
    background: #ffebee;
    color: #c62828;
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const CutHint = styled.div`
  color: #c62828;
  font-size: 12px;
  text-align: center;
`;

const ButtonStyled = styled(Button)`
  && {
    margin: 10px;
  }
`;

const WaveformWrapper = styled.div``;

const Regenerating = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 128px;
  font-style: italic;
`;

const IpaField = styled(TextField)`
  flex: 1;
`;

const PromptField = styled(TextField)`
  && {
    flex-grow: 1;
    margin: 10px 0;
  }
`;

const ItemText = styled.p`
  text-align: center;
`;

export default AudioReviewItem;
